import { useQuery } from "@tanstack/react-query";

import { queryKeys } from "@/queryKeys";
import api from "@/services/api";

/** The providers with a mark of their own (ProviderMark); any other draws a neutral one. */
export type AgentProvider = "openai" | "anthropic" | "google";

/** A model the user can pick (GET agent/models/). */
export interface AgentModel {
  /** What the thread PATCH, message.send and the defaults take as model_ref. */
  ref: string;
  label: string;
  /** Its logo: an AgentProvider, or another name such as the fake model's "steddy". */
  provider: string;
  /** Whether the settings may make it a default: false for the fake model, a thread's pick only. */
  default_ok: boolean;
}

export interface AgentModelCatalog {
  /** The allow-list, in the server's order. */
  models: AgentModel[];
  /** Per thread profile: what a thread without a pick of its own runs on, for this user. */
  defaults: Record<string, string>;
}

/** The picked fake model's ref (backend catalog.FAKE_SLUG under the platform): a thread's pick only. */
export const PICKED_FAKE_REF = "platform:steddy/fake";

// One quick retry, then a load error shows within a second or two (the default backoff takes ~12 s).
const QUICK_RETRY = { retry: 1, retryDelay: 800 } as const;

export const agentModelsQueryOptions = {
  queryKey: queryKeys.agentModels(),
  queryFn: async () => (await api.get<AgentModelCatalog>("/agent/models/")).data,
  staleTime: 5 * 60_000,
  ...QUICK_RETRY,
};

export function useAgentModels(): AgentModelCatalog | undefined {
  return useQuery(agentModelsQueryOptions).data;
}

/** The models a default may be (the settings' choices): the picker's, less the thread-only ones. */
export function defaultChoices(catalog: AgentModelCatalog): AgentModel[] {
  return catalog.models.filter((model) => model.default_ok);
}

export function findModel(catalog: AgentModelCatalog, ref: string | null | undefined) {
  return catalog.models.find((model) => model.ref === ref) ?? null;
}

/**
 * The model a thread answers with: the pick not yet saved, else the thread's own, else this user's
 * default for the thread's profile. A ref that left the allow-list falls back to the default.
 */
export function currentModel(
  catalog: AgentModelCatalog,
  pick: string | undefined,
  threadRef: string | null | undefined,
  profile: string | undefined
): AgentModel | null {
  const fallback = profile ? catalog.defaults[profile] : undefined;
  return findModel(catalog, pick ?? threadRef) ?? findModel(catalog, fallback);
}

const latestPicks = new Map<number, string>();

/**
 * Saves the thread's own pick, one request at a time per thread: picks made meanwhile send only the
 * latest once the request settles, so the server ends on the last click. The server answers every
 * tab with thread.updated; `onFailed` gets each ref it refused.
 */
export function saveThreadModel(
  threadId: number,
  modelRef: string,
  onFailed: (modelRef: string) => void
): void {
  const saving = latestPicks.has(threadId);
  latestPicks.set(threadId, modelRef);
  if (saving) return;
  void (async () => {
    let sent: string | undefined;
    while (latestPicks.get(threadId) !== sent) {
      sent = latestPicks.get(threadId) as string;
      try {
        await api.patch(`/agent/threads/${threadId}/`, { model_ref: sent });
      } catch {
        onFailed(sent);
      }
    }
    latestPicks.delete(threadId);
  })();
}

/**
 * GET|PATCH agent/settings/: the user's default model, the one that applies now, the MCP server's
 * URL for connecting an AI app (null while MCP connections are off or the agent has no usable
 * public URL), and whether WhatsApp and Telegram see their desktop context: their own switch (null
 * follows the organization's) and the organization's, which wins when off.
 */
export interface AgentUserSettings {
  default_model_ref: string;
  mcp_url: string | null;
  desktop_context: boolean | null;
  desktop_context_org: boolean;
}

export const agentSettingsQueryOptions = {
  queryKey: queryKeys.agentSettings(),
  queryFn: async () => (await api.get<AgentUserSettings>("/agent/settings/")).data,
  ...QUICK_RETRY,
};

/** What the daily limit reached says: here, and as the server's budget_exceeded (run or send). */
// The same text as backend apps/agentic/core/runs.py BudgetExceeded.default_detail.
export const LIMIT_REACHED = "Today's AI limit is reached.";

/** GET agent/usage/: today's AI use, a whole percent of the daily limit; null when not shown. */
export interface AgentUsage {
  percent: number | null;
}

// Stale at once: each time the picker opens it asks again, so a finished run shows.
export const agentUsageQueryOptions = {
  queryKey: queryKeys.agentUsage(),
  queryFn: async () => (await api.get<AgentUsage>("/agent/usage/")).data,
  staleTime: 0,
  ...QUICK_RETRY,
};

export const saveDefaultModel = async (modelRef: string) =>
  (await api.patch<AgentUserSettings>("/agent/settings/", { default_model_ref: modelRef })).data;

export const saveDesktopContext = async (on: boolean) =>
  (await api.patch<AgentUserSettings>("/agent/settings/", { desktop_context: on })).data;
